/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import Loading from '../../components/Loading';
import axios from '../../services/axios';

import { Container } from '../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderText,
   PageSubtitle,
   PageTitle,
} from '../../styles/layout/PageHeader';
import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../styles/layout/Section';
import { FormGrid } from '../../styles/layout/FormGrid';
import { Field } from '../../styles/forms/Field';
import { ActionBar, ActionBarRight } from '../../styles/layout/ActionBar';
import { TableWrapper } from '../../styles/table/TableWrapper';
import { Table } from '../../styles/table/Table';
import { EmptyState } from '../../styles/feedback/EmptyState';
import PaginationBar from '../../components/PaginationBar/PaginationBar';
import SectionMeta, { SectionHeaderRow } from '../../components/SectionMeta';
import ProductNameSearch from '../../components/ProductNameSearch';

// Estilos locais
const StatusBadge = styled.span`
   display: inline-block;
   padding: 4px 8px;
   border-radius: 4px;
   font-size: 11px;
   font-weight: 700;
   background: ${({ $status }) => {
      if ($status === 'LOW') return 'rgba(255, 152, 0, 0.12)';
      if ($status === 'OUT') return 'rgba(244, 67, 54, 0.12)';
      return 'rgba(76, 175, 80, 0.12)';
   }};
   color: ${({ $status }) => {
      if ($status === 'LOW') return '#e65100';
      if ($status === 'OUT') return '#c62828';
      return '#2e7d32';
   }};
`;

const StatsGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
   gap: 16px;
   margin-bottom: 20px;
`;

const StatCard = styled.div`
   background: ${({ theme }) => theme.colors.surface};
   border: 1px solid ${({ theme }) => theme.colors.border};
   border-radius: 8px;
   padding: 16px;

   h4 {
      margin: 0 0 8px 0;
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      color: ${({ theme }) => theme.colors.textMuted};
      letter-spacing: 0.5px;
   }

   p {
      margin: 0;
      font-size: 24px;
      font-weight: 700;
      color: ${({ theme }) => theme.colors.textPrimary};
   }

   small {
      font-size: 13px;
      color: ${({ theme }) => theme.colors.textMuted};
   }
`;

export default function PDVSnapshot() {
   const [pdvs, setPdvs] = useState([]);
   const [categories, setCategories] = useState([]);
   const [brands, setBrands] = useState([]);
   const [isLoading, setIsLoading] = useState(false);

   const today = new Date()
      .toLocaleDateString('pt-BR', {
         year: 'numeric',
         month: '2-digit',
         day: '2-digit',
      })
      .split('/')
      .reverse()
      .join('-');

   // Filtros
   const [date, setDate] = useState(today);
   const [pdvId, setPdvId] = useState('');
   const [categoryId, setCategoryId] = useState('');
   const [brandId, setBrandId] = useState('');
   const [productQuery, setProductQuery] = useState('');
   const [selectedProduct, setSelectedProduct] = useState(null);

   // Resultado
   const [snapshot, setSnapshot] = useState(null);

   // Paginação
   const [currentPage, setCurrentPage] = useState(1);
   const itemsPerPage = 20;

   useEffect(() => {
      async function loadData() {
         try {
            setIsLoading(true);

            const [pdvRes, catRes, brandRes] = await Promise.allSettled([
               axios.get('/pdvs'),
               axios.get('/categories'),
               axios.get('/prodbrands'),
            ]);

            // PDVs
            if (pdvRes.status === 'fulfilled') {
               const raw = pdvRes.value?.data ?? [];
               const list = Array.isArray(raw) ? raw : raw?.pdvs ?? [];
               setPdvs(Array.isArray(list) ? list : []);
            }

            // Categorias
            if (catRes.status === 'fulfilled') {
               const raw = catRes.value?.data ?? [];
               setCategories(Array.isArray(raw) ? raw : []);
            }

            // Marcas
            if (brandRes.status === 'fulfilled') {
               const raw = brandRes.value?.data ?? [];
               setBrands(Array.isArray(raw) ? raw : []);
            }
         } catch (err) {
            toast.error('Erro ao carregar dados iniciais.');
         } finally {
            setIsLoading(false);
         }
      }

      loadData();
   }, []);

   const handleClearFilters = () => {
      const ok = window.confirm('Deseja limpar todos os filtros?');
      if (!ok) return;

      setDate(today);
      setPdvId('');
      setCategoryId('');
      setBrandId('');
      setProductQuery('');
      setSelectedProduct(null);
      setSnapshot(null);
      setCurrentPage(1);
   };

   const handleSearch = async (e) => {
      e.preventDefault();

      if (!date) {
         toast.error('Informe a data');
         return;
      }

      if (!pdvId) {
         toast.error('Selecione um PDV');
         return;
      }

      setIsLoading(true);

      try {
         const params = { date };

         if (categoryId) params.category_id = categoryId;
         if (brandId) params.brand_id = brandId;

         // Produto (SKU ou autocomplete) - IGUAL StockEvolution
         const pq = (productQuery || '').trim();
         const isDigits = /^\d+$/.test(pq);

         if (selectedProduct?.prod_sku) {
            // Se selecionou produto no autocomplete, usa o SKU
            params.search = String(selectedProduct.prod_sku);
         } else if (isDigits && pq.length === 7) {
            // SKU completo digitado sem clicar no dropdown
            params.search = pq;
         } else if (pq) {
            // Texto digitado (busca por nome)
            params.search = pq;
         }
         // Se não digitou nada, não adiciona search (busca todos)

         const response = await axios.get(`/stock/snapshot/${pdvId}`, {
            params,
         });

         setSnapshot(response.data);
         setCurrentPage(1);
      } catch (error) {
         const msg =
            error?.response?.data?.errors?.[0] ??
            'Erro ao buscar snapshot do PDV.';
         toast.error(msg);
         setSnapshot(null);
      } finally {
         setIsLoading(false);
      }
   };

   const currentProducts = useMemo(() => {
      if (!snapshot?.products) return [];

      const start = (currentPage - 1) * itemsPerPage;
      const end = start + itemsPerPage;
      return snapshot.products.slice(start, end);
   }, [snapshot, currentPage]);

   const totalPages = snapshot?.products
      ? Math.ceil(snapshot.products.length / itemsPerPage)
      : 0;

   const getStatusLabel = (status) => {
      if (status === 'LOW') return 'Baixo';
      if (status === 'OUT') return 'Esgotado';
      return 'OK';
   };

   return (
      <Container>
         <Loading isLoading={isLoading} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Snapshot do PDV</PageTitle>
               <PageSubtitle>
                  Visualize todo o estoque de um PDV em uma data específica
               </PageSubtitle>
            </PageHeaderText>
         </PageHeader>

         <Section>
            <SectionHeader>
               <SectionTitle>Filtros</SectionTitle>
            </SectionHeader>

            <SectionBody>
               <form onSubmit={handleSearch}>
                  <FormGrid>
                     <Field $col={3} $colMobile={12}>
                        <label>Data *</label>
                        <input
                           type="date"
                           value={date}
                           onChange={(e) => setDate(e.target.value)}
                           max={today}
                           required
                        />
                     </Field>

                     <Field $col={3} $colMobile={12}>
                        <label>PDV *</label>
                        <select
                           value={pdvId}
                           onChange={(e) => setPdvId(e.target.value)}
                           required
                        >
                           <option value="">Selecione...</option>
                           {pdvs.map((pdv) => (
                              <option key={String(pdv.id)} value={pdv.id}>
                                 {pdv.pdv_name ?? pdv.name ?? `${pdv.id}`}
                              </option>
                           ))}
                        </select>
                     </Field>

                     <Field $col={3} $colMobile={12}>
                        <label>Categoria</label>
                        <select
                           value={categoryId}
                           onChange={(e) => setCategoryId(e.target.value)}
                        >
                           <option value="">Todas</option>
                           {categories.map((cat) => (
                              <option key={String(cat.id)} value={cat.id}>
                                 {cat.name}
                              </option>
                           ))}
                        </select>
                     </Field>

                     <Field $col={3} $colMobile={12}>
                        <label>Marca</label>
                        <select
                           value={brandId}
                           onChange={(e) => setBrandId(e.target.value)}
                        >
                           <option value="">Todas</option>
                           {brands.map((brand) => (
                              <option key={String(brand.id)} value={brand.id}>
                                 {brand.name}
                              </option>
                           ))}
                        </select>
                     </Field>

                     <Field $col={8} $colMobile={12}>
                        <label>Buscar Produto (SKU ou nome)</label>
                        <ProductNameSearch
                           value={productQuery}
                           onChange={setProductQuery}
                           onSelect={setSelectedProduct}
                           nameLike={false}
                           displayMode="sku-name"
                           placeholder="Digite o SKU (7 dígitos) ou busque pelo nome..."
                           showHint
                        />
                     </Field>

                     <Field $col={4} $colMobile={12} $alignEnd>
                        <label>&nbsp;</label>
                        <ActionBar>
                           <ActionBarRight>
                              <button
                                 type="button"
                                 onClick={handleClearFilters}
                                 disabled={isLoading}
                              >
                                 Limpar
                              </button>
                              <button type="submit" disabled={isLoading}>
                                 Consultar
                              </button>
                           </ActionBarRight>
                        </ActionBar>
                     </Field>
                  </FormGrid>
               </form>
            </SectionBody>
         </Section>

         {snapshot && (
            <>
               <Section>
                  <SectionHeader>
                     <SectionTitle>Estatísticas</SectionTitle>
                  </SectionHeader>

                  <SectionBody>
                     <StatsGrid>
                        <StatCard>
                           <h4>Total de Produtos</h4>
                           <p>{snapshot.stats.total_products}</p>
                        </StatCard>

                        <StatCard>
                           <h4>Total de Unidades</h4>
                           <p>{snapshot.stats.total_units.toLocaleString('pt-BR')}</p>
                        </StatCard>

                        <StatCard>
                           <h4>Valor Total</h4>
                           <p>
                              {new Intl.NumberFormat('pt-BR', {
                                 style: 'currency',
                                 currency: 'BRL',
                              }).format(snapshot.stats.total_value)}
                           </p>
                           <small>Baseado no custo</small>
                        </StatCard>

                        <StatCard>
                           <h4>Abaixo do Mínimo</h4>
                           <p style={{ color: '#ff9800' }}>
                              {snapshot.stats.low_stock_count}
                           </p>
                        </StatCard>

                        <StatCard>
                           <h4>Esgotados</h4>
                           <p style={{ color: '#f44336' }}>
                              {snapshot.stats.out_of_stock_count}
                           </p>
                        </StatCard>
                     </StatsGrid>
                  </SectionBody>
               </Section>

               <Section>
                  <SectionHeader>
                     <SectionHeaderRow>
                        <SectionTitle>Produtos</SectionTitle>

                        <SectionMeta
                           items={[
                              {
                                 key: 'total',
                                 label: 'Total:',
                                 value: snapshot.total_products,
                              },
                              {
                                 key: 'page',
                                 label: 'Página',
                                 value: `${currentPage}/${totalPages || 1}`,
                              },
                           ]}
                        />
                     </SectionHeaderRow>
                  </SectionHeader>

                  <SectionBody>
                     {currentProducts.length > 0 ? (
                        <>
                           <TableWrapper>
                              <Table>
                                 <thead>
                                    <tr>
                                       <th>SKU</th>
                                       <th>Produto</th>
                                       <th>Categoria</th>
                                       <th>Marca</th>
                                       <th style={{ textAlign: 'right' }}>
                                          Estoque
                                       </th>
                                       <th style={{ textAlign: 'right' }}>
                                          Mínimo
                                       </th>
                                       <th style={{ textAlign: 'center' }}>
                                          Status
                                       </th>
                                    </tr>
                                 </thead>
                                 <tbody>
                                    {currentProducts.map((p) => (
                                       <tr key={String(p.prod_id)}>
                                          <td>{p.prod_sku}</td>
                                          <td>{p.prod_name}</td>
                                          <td>{p.category_name || '-'}</td>
                                          <td>{p.brand_name || '-'}</td>
                                          <td style={{ textAlign: 'right' }}>
                                             {p.stock_qtd}
                                          </td>
                                          <td style={{ textAlign: 'right' }}>
                                             {p.min_stock_qtd}
                                          </td>
                                          <td style={{ textAlign: 'center' }}>
                                             <StatusBadge $status={p.stock_status}>
                                                {getStatusLabel(p.stock_status)}
                                             </StatusBadge>
                                          </td>
                                       </tr>
                                    ))}
                                 </tbody>
                              </Table>
                           </TableWrapper>

                           <PaginationBar
                              currentPage={currentPage}
                              totalPages={totalPages}
                              onPageChange={setCurrentPage}
                              disabled={isLoading}
                           />
                        </>
                     ) : (
                        <EmptyState
                           title="Nenhum produto encontrado"
                           description="Ajuste os filtros e tente novamente."
                        />
                     )}
                  </SectionBody>
               </Section>
            </>
         )}

         {!snapshot && !isLoading && (
            <EmptyState
               title="Nenhum resultado"
               description="Preencha os filtros e clique em Consultar."
            />
         )}
      </Container>
   );
}
